'use client'

import { useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  CloseButton,
  Dialog,
  Grid,
  HStack,
  Input,
  Portal,
  RadioGroup,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import moment from 'moment'

import { AppCard, AppField } from '@/shared'
import {
  useGetBloodGroupsQuery,
  useGetRhFactorsQuery,
} from '@/entities/dictionary'
import type { IBloodGroup, IRhFactor } from '@/entities/dictionary/types'
import { useGetScreeningHistoryQuery } from '@/entities/screening/api'
import { ScreeningHistoryStatusBadge } from '@/entities/screening/ui/ScreeningHistoryStatusBadge'
import { TemperatureDeferralModal } from '@/features/medical-deferral'
import { ETemperatureRecommendation } from '@/entities/visit/model'
import { mapToOptions } from '@/shared/utils/utils'
import { useScreeningForm } from '../model/use-screening-form'
import {
  hasActiveStep,
  isDonorCardReadOnly,
  isVisitClosed,
} from '../model/use-donor-card-read-only'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
} from '@/entities/donor'
import { TemperatureBadge } from '@/entities/donor/ui/TemperatureBadge'

interface Props {
  visitId: string
}

/** Dictionary options as a row of radios — the lists are short enough to show in full. */
function OptionRadioGroup({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: string; label: string }>
  value: string
  onChange: (value: string) => void
}) {
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={(e) => onChange(e.value ?? '')}
      colorPalette="red"
      size="sm"
    >
      <HStack gap={4} flexWrap="wrap" pt={1}>
        {options.map((option) => (
          <RadioGroup.Item
            key={option.value}
            value={option.value}
            cursor="pointer"
          >
            <RadioGroup.ItemHiddenInput />
            <RadioGroup.ItemIndicator />
            <RadioGroup.ItemText fontSize="sm">
              {option.label}
            </RadioGroup.ItemText>
          </RadioGroup.Item>
        ))}
      </HStack>
    </RadioGroup.Root>
  )
}

export function ScreeningTab({ visitId }: Props) {
  const { t, i18n } = useTranslation()
  const [deferralOpen, setDeferralOpen] = useState(false)
  const { data: donor, isPending: donorPending } =
    useGetDonorDetailQuery(visitId)

  const { data: donorCard } = useGetDonorCardDetailQuery(donor?.donor_id ?? '')

  const { form, isPending, onSubmit, labConfigError, closeLabConfigError } =
    useScreeningForm(visitId, donor?.donor_id || '')
  const {
    register,
    control,
    formState: { errors },
  } = form

  const { data: bloodGroups, isPending: bloodGroupsLoading } =
    useGetBloodGroupsQuery()
  const { data: rhFactors, isPending: rhFactorsLoading } =
    useGetRhFactorsQuery()
  const { data: screeningHistory = [], isPending: historyLoading } =
    useGetScreeningHistoryQuery(donor?.donor_id || '')

  const labelKey =
    i18n.language === 'uz'
      ? 'name_uz_lat'
      : i18n.language === 'uz-Cyrl'
        ? 'name_uz_cyr'
        : 'name_ru'

  const bloodGroupOptions = useMemo(
    () =>
      mapToOptions<IBloodGroup>({
        data: bloodGroups?.data,
        valueKey: 'id',
        labelKey,
      }),
    [bloodGroups?.data, i18n.language],
  )

  const rhFactorOptions = useMemo(
    () =>
      mapToOptions<IRhFactor>({
        data: rhFactors?.data,
        valueKey: 'id',
        labelKey,
      }),
    [rhFactors?.data, i18n.language],
  )

  // An abnormal reading is advisory: the therapist already saved the visit, so
  // the nurse decides here whether the donor is turned away over it. A donor
  // who is already deferred («Отвод») has nothing left to decide.
  const temperatureRecommendation =
    donorCard?.latest_vitals?.temperature_recommendation
  const readOnly = isDonorCardReadOnly(donorCard)
  // Nothing can be written to a finished visit, a deferral included.
  const showTemperatureDeferral =
    !readOnly &&
    !isVisitClosed(donorCard) &&
    (temperatureRecommendation === ETemperatureRecommendation.HIGH ||
      temperatureRecommendation === ETemperatureRecommendation.LOW)

  const columns = [
    t('donor_detail.screening.col_datetime'),
    t('donor_detail.screening.col_nurse'),
    t('donor_detail.screening.col_hemoglobin'),
    t('donor_detail.screening.col_blood_group'),
    t('donor_detail.screening.col_status'),
  ]

  return (
    <VStack gap={5} align="stretch">
      {showTemperatureDeferral && (
        <AppCard.Root>
          <AppCard.Body p={5}>
            <HStack
              justify="space-between"
              align={{ base: 'stretch', md: 'center' }}
              gap={4}
              flexDirection={{ base: 'column', md: 'row' }}
            >
              <HStack gap={3} align="center">
                <TemperatureBadge
                  temperature_c={donorCard?.latest_vitals?.temperature_c}
                  temperature_recommendation={temperatureRecommendation}
                />
                <Text fontSize="sm" color="fg.muted">
                  {t(
                    `donor_detail.stats.temperature_recommendation.${temperatureRecommendation}`,
                  )}
                </Text>
              </HStack>

              <Button
                variant="outline"
                colorPalette="red"
                borderRadius="md"
                size="sm"
                px={6}
                flexShrink={0}
                onClick={() => setDeferralOpen(true)}
              >
                {t('donor_detail.screening.temperature_deferral.button')}
              </Button>
            </HStack>
          </AppCard.Body>
        </AppCard.Root>
      )}

      <Dialog.Root
        open={!!labConfigError}
        onOpenChange={({ open }) => {
          if (!open) closeLabConfigError()
        }}
        role="alertdialog"
        size="sm"
        placement="center"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bg="bg.surface">
              <Dialog.Header pb={2}>
                <Dialog.Title fontSize="lg" fontWeight="bold" pr={8}>
                  {t('donor_detail.screening.lab_config_error.title')}
                </Dialog.Title>
                <Dialog.CloseTrigger
                  asChild
                  position="absolute"
                  top={4}
                  right={4}
                >
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Dialog.Header>
              <Dialog.Body py={2}>
                <Text fontSize="sm">{labConfigError}</Text>
              </Dialog.Body>
              <Dialog.Footer pt={4} justifyContent="flex-end">
                <Button
                  bg="primary.solid"
                  color="white"
                  borderRadius="md"
                  size="sm"
                  px={6}
                  _hover={{ opacity: 0.85 }}
                  _active={{ opacity: 0.7 }}
                  onClick={closeLabConfigError}
                >
                  {t('donor_detail.screening.lab_config_error.ok')}
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      <TemperatureDeferralModal
        open={deferralOpen}
        visitId={visitId}
        donorId={donor?.donor_id || ''}
        onClose={() => setDeferralOpen(false)}
      />

      {hasActiveStep(donorCard, 'SCREENING') && !readOnly && (
        <AppCard.Root>
          <AppCard.Body p={5}>
            <Text fontWeight="semibold" fontSize="md" mb={4}>
              {t('donor_detail.screening.data_entry_title')}
            </Text>

            <form onSubmit={onSubmit}>
              <Grid
                templateColumns={{ base: '1fr', md: '1fr 1fr 1fr' }}
                gap={4}
              >
                <Box>
                  <AppField.Root invalid={!!errors.hemoglobin}>
                    <AppField.Label>
                      {t('donor_detail.screening.hemoglobin_label')}
                    </AppField.Label>
                    <Input
                      type="number"
                      placeholder={t(
                        'donor_detail.screening.hemoglobin_placeholder',
                      )}
                      borderRadius="md"
                      size="sm"
                      {...register('hemoglobin')}
                    />
                    <AppField.ErrorText>
                      {errors.hemoglobin?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Box>

                <Box>
                  <AppField.Root invalid={!!errors.blood_group_id}>
                    <AppField.Label>
                      {t('donor_detail.screening.blood_group_label')}
                    </AppField.Label>
                    <Controller
                      control={control}
                      name="blood_group_id"
                      render={({ field }) =>
                        bloodGroupsLoading ? (
                          <Spinner size="sm" color="primary.fg" />
                        ) : (
                          <OptionRadioGroup
                            options={bloodGroupOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )
                      }
                    />
                    <AppField.ErrorText>
                      {errors.blood_group_id?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Box>

                <Box>
                  <AppField.Root invalid={!!errors.rh_factor_id}>
                    <AppField.Label>
                      {t('donor_detail.screening.rh_factor_label')}
                    </AppField.Label>
                    <Controller
                      control={control}
                      name="rh_factor_id"
                      render={({ field }) =>
                        rhFactorsLoading ? (
                          <Spinner size="sm" color="primary.fg" />
                        ) : (
                          <OptionRadioGroup
                            options={rhFactorOptions}
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )
                      }
                    />
                    <AppField.ErrorText>
                      {errors.rh_factor_id?.message}
                    </AppField.ErrorText>
                  </AppField.Root>
                </Box>
              </Grid>

              <HStack justify="flex-end" mt={4}>
                <Button
                  type="submit"
                  bg="primary.solid"
                  color="white"
                  borderRadius="md"
                  size="sm"
                  px={6}
                  loading={isPending}
                  _hover={{ opacity: 0.85 }}
                  _active={{ opacity: 0.7 }}
                >
                  {t('donor_detail.screening.save')}
                </Button>
              </HStack>
            </form>
          </AppCard.Body>
        </AppCard.Root>
      )}
      <AppCard.Root>
        <AppCard.Body p={5}>
          <Text fontWeight="semibold" fontSize="md" mb={4}>
            {t('donor_detail.screening.history_title')}
          </Text>

          <Grid
            templateColumns="1fr 1fr 1fr 1fr 1fr"
            gap={2}
            pb={2}
            borderBottomWidth="1px"
            borderColor="border"
          >
            {columns.map((col, index) => (
              <Text
                key={`${col}-${index}`}
                fontSize="xs"
                color="fg.muted"
                fontWeight="semibold"
              >
                {col}
              </Text>
            ))}
          </Grid>

          <VStack gap={0} align="stretch">
            {historyLoading ? (
              <Text fontSize="sm" color="fg.muted" py={4}>
                {t('donor_detail.screening.history_loading')}
              </Text>
            ) : screeningHistory.length === 0 ? (
              <Text fontSize="sm" color="fg.muted" py={4}>
                {t('donor_detail.screening.history_empty')}
              </Text>
            ) : (
              screeningHistory.map((row) => (
                <Grid
                  key={row.screening_id}
                  templateColumns="1fr 1fr 1fr 1fr 1fr"
                  gap={2}
                  py={3}
                  borderBottomWidth="1px"
                  borderColor="border.muted"
                  _last={{ borderBottom: 'none' }}
                  alignItems="center"
                >
                  <Text fontSize="sm" color="fg.muted">
                    {moment(row.screened_at).format('YYYY-MM-DD HH:mm')}
                  </Text>
                  <Text fontSize="sm">{row.nurse_fio}</Text>
                  <Text fontSize="sm">{row.hemoglobin}</Text>
                  <Text fontSize="sm" color="fg.muted" truncate>
                    {row.rh_factor_name} {row.blood_group_name}
                  </Text>
                  <ScreeningHistoryStatusBadge status={row.result} />
                  {/* <IconButton
                    variant="ghost"
                    size="xs"
                    aria-label={t('donor_detail.screening.view_details')}
                    color="fg.muted"
                    _hover={{ color: 'fg', bg: 'bg.muted' }}
                  >
                    <Eye size={15} />
                  </IconButton> */}
                </Grid>
              ))
            )}
          </VStack>
        </AppCard.Body>
      </AppCard.Root>
    </VStack>
  )
}
